import React, { useState } from 'react';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { groupsAPI } from '../../../services/api';
import MemberSelectionModal from '../Members/MemberSelectionModal';
import { formatGroupType } from '../../../utils/formatters';
import LocationSelector from '../../common/Forms/LocationSelector';
import './AddGroupForm.css';

function AddGroupForm({ onClose, onGroupAdded }) {
    const [formData, setFormData] = useState({
        name: '',
        type: 'club',
        country: '',
        city: '',
        description: '',
        members: [],
        socialLinks: {
            facebook: '',
            instagram: ''
        }
    });
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showMemberSelection, setShowMemberSelection] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith('social-')) {
            const platform = name.replace('social-', '');
            setFormData(prev => ({
                ...prev,
                socialLinks: {
                    ...prev.socialLinks,
                    [platform]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
            [name]: value
            }));
        }
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5242880) { // 5MB limit
                setError('Logo size should be less than 5MB');
                return;
            }
            setLogo(file);
            setError('');
        }
    };

    const handleMemberSelection = (selectedMembers) => {
        setFormData(prev => ({
            ...prev,
            members: selectedMembers
        }));
        setShowMemberSelection(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {            
            // If we have a logo, use FormData to send the file
            if (logo) {
                const formDataToSend = new FormData();
                // Add each form field individually to FormData
                Object.keys(formData).forEach(key => {
                    if (key === 'socialLinks') {
                        // Handle nested object
                        Object.keys(formData.socialLinks).forEach(socialKey => {
                            formDataToSend.append(`socialLinks.${socialKey}`, formData.socialLinks[socialKey]);
                        });
                    } else if (key === 'members') {
                        // Handle array
                        formDataToSend.append('members', JSON.stringify(formData.members));
                    } else {
                        formDataToSend.append(key, formData[key]);
                    }
                });
                formDataToSend.append('createdAt', new Date().toISOString());
                formDataToSend.append('logo', logo);
                
                await groupsAPI.createGroup(formDataToSend);
            } else {
                // If no logo, just send the JSON data directly
                await groupsAPI.createGroup({
                    ...formData,
                    createdAt: new Date().toISOString()
                });
            }
            
            onGroupAdded();
            onClose();
        } catch (error) {
            setError(error.message || 'Failed to add group');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-group-form-overlay">
            <div className="add-group-form">
                <h2>Add New Group</h2>
                {error && <div className="error-message">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Group Type *</label>
                        <select
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="club">{formatGroupType('club')}</option>
                            <option value="localCouncil">{formatGroupType('localCouncil')}</option>
                            <option value="projectTeam">{formatGroupType('projectTeam')}</option>
                            <option value="cityTeam">{formatGroupType('cityTeam')}</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Name *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Location</label>
                        <LocationSelector
                            countryCode={formData.country}
                            city={formData.city}
                            onCountryChange={(code) => {
                                setFormData(prev => ({
                                    ...prev,
                                    country: code,
                                    city: ''
                                }));
                            }}
                            onCityChange={(cityName) => {
                                setFormData(prev => ({
                                    ...prev,
                                    city: cityName
                                }));
                            }}
                        />
                    </div>

                    <div className="form-group">
                        <label>Description *</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            required
                            rows="4"
                        />
                    </div>

                    <div className="form-group">
                        <label>Members</label>
                        <button 
                            type="button" 
                            className="select-members-button"
                            onClick={() => setShowMemberSelection(true)}
                        >
                            Select Members ({formData.members.length} selected)
                        </button>
                    </div>

                    <div className="form-group">
                        <label>Logo</label>
                        <div className="logo-upload-container">
                            {logoPreview ? (
                                <div className="logo-preview">
                                    <img src={logoPreview} alt="Logo preview" />
                                    <button 
                                        type="button" 
                                        className="remove-logo"
                                        onClick={() => {
                                            setLogo(null);
                                            setLogoPreview(null);
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            ) : (
                                <div className="logo-upload-box">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogoChange}
                                        id="logo-upload"
                                        className="hidden-input"
                                    />
                                    <label htmlFor="logo-upload" className="upload-label">
                                        <i className="fas fa-cloud-upload-alt"></i>
                                        <span>Click to upload logo</span>
                                        <span className="upload-hint">Recommended: 400x400px</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Facebook Link</label>
                        <input
                            type="url"
                            name="social-facebook"
                            value={formData.socialLinks.facebook}
                            onChange={handleInputChange}
                            placeholder="https://facebook.com/..."
                        />
                    </div>

                    <div className="form-group">
                        <label>Instagram Link</label>
                        <input
                            type="url"
                            name="social-instagram"
                            value={formData.socialLinks.instagram}
                            onChange={handleInputChange}
                            placeholder="https://instagram.com/..."
                        />
                    </div>

                    <div className="form-actions">
                        <button type="submit" disabled={loading}>
                            {loading ? 'Adding...' : 'Add Group'}
                        </button>
                        <button type="button" onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            {showMemberSelection && (
                <MemberSelectionModal
                    currentMembers={formData.members}
                    onSelect={handleMemberSelection}
                    onClose={() => setShowMemberSelection(false)}
                />
            )}
        </div>
    );
}

export default AddGroupForm; 